<template>
  <panel
    class="case-interventions"
    title="Interventions"
    color="green-1"
    scroll-mode="content"
  >
    <template v-slot:title>
      <div class="stance-container">
        <div class="selector-row">
          <help-tooltip type="info">
            Certain interventions are only available in some positions.
          </help-tooltip>

          <position-selector
            :value="getStateValue('position')"
            :disabled="readonly"
            :compact="true"
            :valid-stances="state.availableStances"
            @input="value => setStateValue('position', value)"
          />
        </div>
        <span class="position-label">Starting Position</span>
      </div>
    </template>

    <div class="list-row">
      <switch-list
        :items="categoryItems"
        title="Intervention List"
        subtitle="Select an intervention to modify details"
        :selected="state.selectedCategory"
        :readonly="readonly"
        @toggle="toggleCategory"
        @select="expandCategory"
      />
      <switch-list
        v-if="treatmentItems.length"
        :items="treatmentItems"
        title="Treatment List"
        subtitle="Select a treatment to modify details"
        :selected="state.selectedTreatment"
        :readonly="readonly"
        :can-close="true"
        @toggle="toggleTreatment"
        @select="expandTreatment"
        @mouseOver="mouseOverTreatment"
        @mouseLeave="mouseLeaveTreatment"
        @close="closeTreatments"
      />
      <switch-list
        v-if="typeItems.length"
        :items="typeItems"
        title="Type List"
        subtitle="Select a type to modify details"
        :selected="state.selectedType"
        :readonly="readonly"
        :can-close="true"
        @toggle="toggleType"
        @select="expandType"
        @close="closeTypes"
      />
      <switch-list
        v-if="locationItems.length"
        :items="locationItems"
        title="Location List"
        subtitle="Select a location to modify details"
        :readonly="readonly"
        :can-close="true"
        @mouseOver="mouseOverLocation"
        @mouseLeave="mouseLeaveLocation"
        @toggle="toggleLocation"
        @close="closeLocations"
      />
      <switch-list
        v-if="componentItems.length"
        :items="componentItems"
        title="Component List"
        subtitle="Select to turn on components"
        :readonly="readonly"
        :can-close="true"
        @toggle="toggleComponent"
        @close="closeComponents"
      />
    </div>
  </Panel>
</template>

<script>
import { uniq, merge, get, toArray, intersection } from 'lodash';
import {
  interventions,
  categoryLabel,
  treatmentLabel,
  typeLabel,
  locationLabel,
} from '~lib/common/config/interventions';
import isApplicableMoulage from '~lib/common/utils/isApplicableMoulage';
import makeMoulageLabel from '~lib/common/utils/makeMoulageLabel';
import HelpTooltip from '~lib/common/components/HelpTooltip.vue';
import Panel from '../../common/Panel';
import SwitchList from '../../common/SwitchList/SwitchList.vue';
import PositionSelector from '../../common/PositionSelector.vue';

export default {
  name: 'case-interventions',
  components: {
    Panel,
    SwitchList,
    PositionSelector,
    HelpTooltip,
  },
  props: {
    caseConf: {
      type: Object,
      required: true,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      state: {
        selectedCategory: null,
        selectedTreatment: null,
        selectedType: null,
        availableStances: null,
      },
    };
  },
  computed: {
    caseInterventions() {
      return (this.caseConf.availableInterventions || []).map(caseIntv => {
        return interventions.find(intv => intv.key === caseIntv.key);
      });
    },
    caseMoulages() {
      return merge({}, this.caseConf.moulages, this.caseConf.structuralMoulages);
    },
    categories() {
      return uniq(interventions.map(intv => intv.category));
    },
    treatments() {
      return uniq(
        interventions
          .filter(intv => intv.category === this.state.selectedCategory)
          .map(intv => intv.treatment)
      );
    },
    categoryItems() {
      return this.categories.map(c => {
        const childIntvs = interventions.filter(intv => intv.category === c);
        const isDisabled = !isApplicableMoulage(childIntvs, this.caseMoulages);
        const isOn = !!(this.caseInterventions.find(caseIntv => caseIntv.category === c));
        return {
          key: c,
          label: categoryLabel[c].en,
          showCaret: true,
          on: isOn,
          disabled: isDisabled && !isOn,
          tooltip: 'No applicable moulage selected',
          error: isDisabled && isOn,
        };
      });
    },
    treatmentItems() {
      return this.treatments.map(treatment => {
        const childIntvs = interventions.filter(intv => intv.treatment === treatment);
        const isOn = !!(this.caseInterventions.find(caseIntv => caseIntv.treatment === treatment));
        const isApplicable = isApplicableMoulage(childIntvs, this.caseMoulages);
        const isStanceLimited = this.isTreatmentStanceLimited(treatment);
        const incompatibleMoulage = this.getFirstIncompatibleMoulage(treatment);
        const isDisabled = !isApplicable || isStanceLimited || incompatibleMoulage;
        let tooltip = '';
        if (!isApplicable) {
          tooltip = 'No applicable moulage selected';
        } else if (isStanceLimited) {
          tooltip = 'Intervention requires patient to be in specific position to use';
        } else if (incompatibleMoulage) {
          tooltip = `Intervention can't be used with selected moulages (${makeMoulageLabel(incompatibleMoulage)})`;
        }
        return {
          key: treatment,
          label: treatmentLabel[treatment].en,
          showCaret: childIntvs.length > 1,
          on: isOn,
          disabled: isDisabled && !isOn,
          tooltip: tooltip,
          error: isDisabled && isOn,
        };
      });
    },
    typeItems() {
      const typeIntvs = interventions.filter(
        intv => intv.type && intv.treatment === this.state.selectedTreatment
      );
      if (!typeIntvs) {
        return [];
      }
      return typeIntvs.map(typeIntv => {
        const childIntvs = interventions.filter(intv => intv.type === typeIntv.type);
        const isOn = !!(this.caseInterventions.find(caseIntv => caseIntv.key === typeIntv.key));
        const isApplicable = isApplicableMoulage(childIntvs, this.caseMoulages);
        const isStanceLimited = this.isStanceLimited(typeIntv);
        const isDisabled = !isApplicable || isStanceLimited;
        let tooltip = '';
        if (!isApplicable) {
          tooltip = 'No applicable moulage selected';
        } else if (isStanceLimited) {
          tooltip = 'Intervention requires patient to be in specific position to use';
        }
        return {
          key: typeIntv.key,
          label: typeLabel[typeIntv.type].en,
          showCaret: !!(typeIntv.components),
          on: isOn,
          disabled: isDisabled && !isOn,
          tooltip: tooltip,
          error: isDisabled && isOn,
        };
      });
    },
    componentItems() {
      const intv = interventions.find(
        intv => intv.components && intv.key === this.state.selectedType
      );
      if (!intv) {
        return [];
      }
      const availIntv = (this.caseConf.availableInterventions || []).find(caseIntv => caseIntv.key === intv.key);
      return intv.components.map(comp => {
        const isOn = !!(availIntv && availIntv.availableComponents && availIntv.availableComponents.find(ac => ac.key === comp.key));
        const isDisabled = !isApplicableMoulage([intv], this.caseMoulages);
        return {
          key: comp.key,
          label: comp.label.en,
          on: isOn,
          disabled: isDisabled && !isOn,
          tooltip: 'No applicable moulage selected',
          error: isDisabled && isOn,
        };
      });
    },
    behaviorChangeNodes() {
      const nodes = get(this.caseConf, 'caseLogicDescription.nodes', []);
      return toArray(nodes).filter(
        (node) => {
          if (node.name === 'Effect') {
            const effectType = get(node, 'data.effect.type');
            return  effectType === 'ANIMATION_REPEAT' || effectType === 'ANIMATION_ONE_SHOT'
          }
          return false;
        }
      );
    },
    locationItems() {
      const locationIntvs = interventions.filter(
        intv => intv.location && intv.treatment === this.state.selectedTreatment
      );
      if (!locationIntvs) {
        return [];
      }
      return locationIntvs.map(locationIntv => {
        const isOn = !!(this.caseInterventions.find(caseIntv => caseIntv.key === locationIntv.key));
        const isApplicable = isApplicableMoulage([locationIntv], this.caseMoulages);
        const isStanceLimited = this.isStanceLimited(locationIntv);
        const isDisabled = !isApplicable || isStanceLimited;
        let tooltip = '';
        if (!isApplicable) {
          tooltip = 'No applicable moulage selected';
        } else if (isStanceLimited) {
          tooltip = 'Intervention requires patient to be in specific position to use';
        }
        return {
          key: locationIntv.key,
          label: locationLabel[locationIntv.key].en,
          showCaret: false,
          on: isOn,
          disabled: isDisabled && !isOn,
          tooltip: tooltip,
          error: isDisabled && isOn,
        };
      });
    },
  },
  methods: {
    closeTreatments() {
      this.state.selectedCategory = null;
      this.state.selectedTreatment = null;
      this.state.selectedType = null;
    },
    closeTypes() {
      this.state.selectedTreatment = null;
      this.state.selectedType = null;
    },
    closeComponents() {
      this.state.selectedType = null;
    },
    closeLocations() {
      this.state.selectedTreatment = null;
    },
    expandCategory(item) {
      this.state.selectedCategory = item.key;
      this.state.selectedTreatment = null;
      this.state.selectedType = null;
    },
    expandTreatment(item) {
      this.state.selectedTreatment = item.key;
      this.state.selectedType = null;
    },
    expandType(item) {
      this.state.selectedType = item.key;
    },
    toggleCategory(item, value) {
      if (value) {
        interventions
          .filter(intv => intv.category === item.key)
          .forEach(intv => {
            if (isApplicableMoulage([intv], this.caseMoulages) && !this.isStanceLimited(intv)) {
              this.pushIntervetion({
                available: true,
                key: intv.key,
                availableComponents: intv.components && intv.components.map(c => ({ key: c.key })),
              });
            }
          });
      } else {
        this.caseConf.availableInterventions = this.caseConf.availableInterventions.filter(availIntv => {
          const intervention = interventions.find(intv => intv.key === availIntv.key);
          return intervention.category !== item.key;
        });
      }
      this.emitUpdate();
    },
    toggleTreatment(item, value) {
      if (value) {
        interventions
          .filter(intv => intv.treatment === item.key)
          .forEach(intv => {
            if (isApplicableMoulage([intv], this.caseMoulages) && !this.isStanceLimited(intv)) {
              this.pushIntervetion({
                available: true,
                key: intv.key,
                availableComponents: intv.components && intv.components.map(c => ({ key: c.key })),
              });
            }
          });
      } else {
        this.caseConf.availableInterventions = this.caseConf.availableInterventions.filter(availIntv => {
          const intervention = interventions.find(intv => intv.key === availIntv.key);
          return intervention.treatment !== item.key;
        });
      }
      this.emitUpdate();
    },
    toggleType(item, value) {
      if (value) {
        const intv = interventions.find(intv => intv.key === item.key);
        this.pushIntervetion({
          available: true,
          key: intv.key,
          availableComponents: intv.components && intv.components.map(c => ({ key: c.key })),
        });
      } else {
        this.caseConf.availableInterventions = this.caseConf.availableInterventions.filter(availIntv => {
          return availIntv.key !== item.key;
        });
      }
      this.emitUpdate();
    },
    toggleComponent(item, value) {
      let intv = (this.caseConf.availableInterventions || []).find(intv => intv.key === this.state.selectedType);
      if (value) {
        if (!intv) {
          intv = {
            available: true,
            key: this.state.selectedType,
            availableComponents: [],
          };
          this.pushIntervetion(intv);
        }
        intv.availableComponents = intv.availableComponents.concat({ key: item.key  });
      } else {
        intv.availableComponents = intv.availableComponents.filter(c => c.key !== item.key)
      }
      this.emitUpdate();
    },
    toggleLocation(item, value) {
      if (value) {
        const intv = interventions.find(intv => intv.key === item.key);
        this.pushIntervetion({
          available: true,
          key: intv.key,
        });
      } else {
        this.caseConf.availableInterventions = this.caseConf.availableInterventions.filter(availIntv => {
          return availIntv.key !== item.key;
        });
      }
      this.emitUpdate();
    },
    pushIntervetion(intv) {
      if (!this.caseConf.availableInterventions) {
        this.$set(this.caseConf, 'availableInterventions', []);
      }
      this.caseConf.availableInterventions.push(intv);
    },
    mouseOverTreatment(item) {
      const treatment = interventions.find(intv => intv.treatment === item.key);
      if (treatment && !treatment.location && treatment.availableStances) {
        this.state.availableStances = treatment.availableStances;
      }
    },
    mouseLeaveTreatment() {
      this.state.availableStances = null;
    },
    mouseOverLocation(item) {
      const location = interventions.find(intv => intv.key === item.key);
      if (location && location.availableStances) {
        this.state.availableStances = location.availableStances;
      }
    },
    mouseLeaveLocation() {
      this.state.availableStances = null;
    },
    getStateValue(stateId, caseConf = this.caseConf) {
      if (!caseConf || !caseConf._initialStates) {
        return undefined;
      }
      return get(
        caseConf._initialStates.find(s => s.id === stateId),
        'options.value',
      );
    },
    isTreatmentStanceLimited(treatment) {
      const intv = interventions.find(intv => intv.treatment === treatment);
      return !intv.location && this.isStanceLimited(intv);
    },
    getFirstIncompatibleMoulage(treatment) {
      const intv = interventions.find(intv => intv.treatment === treatment);
      if (intv) {
        const moulages = intersection(intv.incompatibleMoulages, Object.values(this.caseMoulages));
        if (moulages.length > 0) {
          return moulages[0];
        }
      }
    },
    isStanceLimited(intv) {
      if (intv && intv.availableStances) {
        const position = this.getStateValue('position');
        if (!intv.availableStances.includes(position)) {
          return true;
        }
        for (const node of this.behaviorChangeNodes) {
          const position = get(node, 'data.effect.position');
          if (!intv.availableStances.includes(position)) {
            return true;
          }
        }
      }
      return false;
    },
    emitUpdate() {
      this.$emit('update', {
        availableInterventions: this.caseConf.availableInterventions
      });
    },
  },
}
</script>

<style scoped>
.case-interventions {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
}
.case-interventions >>> .title-container {
  justify-content: space-between;
  align-items: flex-start;
}
.case-interventions >>> .panel-content {
  padding-top: 6px;
}
.title-row {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  justify-content: space-between;
  padding-bottom: 6px;
}
.list-row {
  display: flex;
  height: 100%;
}
.switch-list:not(:first-child) {
  margin-left: 20px;
}
.stance-container {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
}
.position-label {
  color: black;
  margin-top: 4px;
}
.help-tooltip {
  margin-right: 10px;
}
.selector-row {
  display: flex;
  align-items: center;
}
</style>
